import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

/** Mirrors `SignatureResolution` in apps/email/services/mailbox_types.py. */
export type SignatureResolutionStatus =
  | 'resolved'
  | 'not_configured'
  | 'disabled'
  | 'unavailable'
  | 'no_permission';

export interface AccountSignature {
  social_account_id: number;
  account_email: string;
  provider: string;
  status: SignatureResolutionStatus;
  /** Sanitised server-side; blank for every status except `resolved`. */
  html: string;
}

export interface AccountSignatureResponse {
  signatures: AccountSignature[];
}

/**
 * The caller's own connected mailboxes and the signature that would be
 * inserted for each.
 *
 * A signature changes rarely and is only read to seed a draft, so this is
 * cached aggressively — refetching per composer-open would put a Gmail API
 * round-trip on an interactive path for a value that almost never changes.
 */
export function useAccountSignatures(orgId: string | number | undefined) {
  return useQuery({
    queryKey: ['orgs', String(orgId), 'email', 'signatures'],
    queryFn: () =>
      apiGet<AccountSignatureResponse>(`/api/orgs/${orgId}/email/signature/`),
    enabled: Boolean(orgId),
    staleTime: 15 * 60 * 1000,
    retry: false,
  });
}

/**
 * The `AccountSignature` matched for `socialAccountId`, or null when there is
 * nothing to insert.
 *
 * Returns null for every non-`resolved` status. `unavailable` deliberately
 * matches nothing rather than guessing — a failed resolution is not the same
 * as "no signature", and seeding an empty block would assert the wrong thing
 * in the draft.
 *
 * Exposed separately from `selectSignatureHtml` so callers that need more
 * than the HTML (e.g. the matched account's `provider`) can derive it from
 * the same account pick instead of re-implementing the gating.
 */
export function selectSignature(
  data: AccountSignatureResponse | undefined,
  socialAccountId: number | null | undefined
): AccountSignature | null {
  if (!data) {
    return null;
  }
  const { signatures } = data;
  if (!signatures || signatures.length === 0) {
    return null;
  }
  const match =
    (socialAccountId != null
      ? signatures.find(s => s.social_account_id === socialAccountId)
      : undefined) ?? signatures[0];
  if (!match || match.status !== 'resolved' || !match.html) {
    return null;
  }
  return match;
}

/**
 * The signature HTML to seed for `socialAccountId`, or null when there is
 * nothing to insert. See `selectSignature` for the matching/gating rules.
 */
export function selectSignatureHtml(
  data: AccountSignatureResponse | undefined,
  socialAccountId: number | null | undefined
): string | null {
  return selectSignature(data, socialAccountId)?.html ?? null;
}
